/**
 * DOMI-CARE - Módulo 1: Pacientes y Admisión Domiciliaria
 * Admisión integral, datos demográficos, cobertura y evaluación de accesibilidad.
 */

import { offlineStore } from '../../api/offlineStore.js';
import { ModalManager } from '../../components/modal.js';
import { notify } from '../../components/notificationCenter.js';
import { getObrasSocialesList, registrarNuevaObraSocial } from '../../data/obrasSocialesData.js';

export async function renderPacientesView(container, activePatientId, onSelectPatient) {
  const pacientes = await offlineStore.getAll('pacientes');

  container.innerHTML = `
    <div class="view-header">
      <div>
        <h1 class="view-title">Padrón de Pacientes y Admisión</h1>
        <p class="view-subtitle">Gestión integral de internaciones domiciliarias activas y accesibilidad edilicia</p>
      </div>
      <div class="view-actions">
        <button id="btn-nueva-admision" class="btn btn-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
          Nueva Admisión Domiciliaria
        </button>
      </div>
    </div>

    <!-- Buscador y Filtros -->
    <div class="card" style="margin-bottom: 1.5rem; padding: 0.85rem 1.25rem;">
      <div style="display: flex; gap: 1rem; align-items: center; flex-wrap: wrap;">
        <input type="text" id="filtro-paciente" class="form-control" placeholder="🔍 Buscar por nombre, DNI, obra social o localidad..." style="flex: 1; min-width: 260px;">
        <div style="display: flex; gap: 0.5rem;">
          <span class="badge badge-primary" style="padding: 0.4rem 0.8rem; font-size: 0.8rem;">${pacientes.length} Pacientes Activos</span>
        </div>
      </div>
    </div>

    <!-- Lista de Tarjetas de Pacientes -->
    <div class="patients-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 1.25rem;">
      ${pacientes.map(p => renderPatientCard(p, p.id === activePatientId)).join('')}
    </div>

    <!-- Modal de Nueva Admisión -->
    ${renderModalNuevaAdmision()}
  `;

  // Event Listeners
  document.getElementById('btn-nueva-admision').addEventListener('click', () => {
    ModalManager.open('modal-nueva-admision');
  });

  document.querySelectorAll('.btn-select-paciente').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const pid = e.currentTarget.dataset.id;
      onSelectPatient(pid);
      notify.info(`Paciente seleccionado: ${pid}`);
      renderPacientesView(container, pid, onSelectPatient);
    });
  });

  // Filtro en vivo
  const searchInput = document.getElementById('filtro-paciente');
  searchInput.addEventListener('input', (e) => {
    const val = e.target.value.toLowerCase();
    const cards = container.querySelectorAll('.patient-card-item');
    cards.forEach(card => {
      const txt = card.innerText.toLowerCase();
      card.style.display = txt.includes(val) ? '' : 'none';
    });
  });

  // Submit Nueva Admisión
  const formAdmision = document.getElementById('form-nueva-admision');
  if (formAdmision) {
    formAdmision.addEventListener('submit', async (e) => {
      e.preventDefault();
      await handleGuardarAdmision(formAdmision, onSelectPatient);
    });
  }

  // Botón Agregar Nueva Obra Social
  const btnNuevaOS = document.getElementById('btn-nueva-obra-social');
  if (btnNuevaOS) {
    btnNuevaOS.addEventListener('click', () => {
      const inputOS = document.getElementById('input-obra-social');
      const sugerido = inputOS ? inputOS.value.trim() : '';
      const nombre = prompt('Ingrese el nombre o sigla de la nueva Obra Social / Prepaga a incorporar:', sugerido);
      if (nombre && nombre.trim()) {
        const res = registrarNuevaObraSocial(nombre);
        if (res.success) {
          actualizarDatalistObrasSociales();
          if (inputOS) inputOS.value = res.name;
          notify.success(res.isNew ? `✓ Obra social "${res.name}" añadida al catálogo permanente.` : `La obra social "${res.name}" ya se encuentra registrada.`);
        } else {
          notify.error(res.error || 'Error al registrar obra social');
        }
      }
    });
  }
}

function actualizarDatalistObrasSociales() {
  const datalist = document.getElementById('lista-obras-sociales');
  if (datalist) {
    const list = getObrasSocialesList();
    datalist.innerHTML = list.map(os => `<option value="${os}"></option>`).join('');
  }
}

function renderPatientCard(p, isSelected) {
  const compClass = p.nivelComplejidad === 'Alta Complejidad' ? 'badge-danger' : 
                    p.nivelComplejidad === 'Mediana Complejidad' ? 'badge-warning' : 'badge-primary';

  return `
    <div class="card patient-card-item ${isSelected ? 'selected' : ''}" style="${isSelected ? 'border: 2px solid var(--primary-600); box-shadow: 0 0 0 3px var(--primary-100);' : ''}">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.75rem;">
        <div>
          <span class="badge ${compClass}" style="margin-bottom: 0.4rem;">${p.nivelComplejidad}</span>
          <h3 style="font-size: 1.1rem; font-weight: 700; color: var(--text-primary);">${p.nombreCompleto}</h3>
          <div style="font-size: 0.8rem; color: var(--text-muted); font-family: var(--font-mono);">DNI: ${p.dni} • ${p.edad || 70} años (${p.genero})</div>
        </div>
        <span class="badge badge-success">${p.obraSocial}</span>
      </div>

      <div style="background: var(--slate-50); padding: 0.75rem; border-radius: var(--radius-md); font-size: 0.825rem; margin-bottom: 0.85rem; border: 1px solid var(--border-subtle);">
        <div style="font-weight: 600; color: var(--text-primary); margin-bottom: 0.2rem;">📍 ${p.direccion} (${p.localidad})</div>
        <div style="color: var(--text-muted); font-size: 0.78rem;">${p.indicacionesLlegada || 'Sin indicaciones adicionales'}</div>
      </div>

      <div style="font-size: 0.825rem; margin-bottom: 1rem;">
        <div style="color: var(--primary-800); font-weight: 600; margin-bottom: 0.2rem;">
          🩺 ${p.diagnosticoPrincipal ? p.diagnosticoPrincipal.desc : 'Diagnóstico en valoración'}
        </div>
        <div style="color: var(--text-muted); font-size: 0.78rem;">
          👤 Cuidador: <strong>${p.accesibilidad?.cuidadorNombre || 'Familiar'}</strong> (${p.accesibilidad?.cuidadorVinculo || 'Familiar'}) - 📞 ${p.accesibilidad?.cuidadorTelefono || p.telefono}
        </div>
      </div>

      <div style="display: flex; gap: 0.5rem;">
        <button class="btn ${isSelected ? 'btn-secondary' : 'btn-primary'} btn-sm btn-select-paciente" data-id="${p.id}" style="flex: 1;">
          ${isSelected ? '✓ Paciente en Foco' : 'Seleccionar Paciente'}
        </button>
        <a href="https://maps.google.com/?q=${encodeURIComponent(p.direccion + ', ' + p.localidad)}" target="_blank" class="btn btn-secondary btn-sm" title="Ver en Google Maps" style="padding: 0.35rem 0.6rem;">
          🗺️ GPS
        </a>
      </div>
    </div>
  `;
}

function renderModalNuevaAdmision() {
  return `
    <div id="modal-nueva-admision" class="modal-overlay">
      <div class="modal-container" style="max-width: 750px;">
        <div class="modal-header">
          <h2 style="font-size: 1.15rem; font-weight: 700; color: var(--text-primary); display: flex; align-items: center; gap: 0.5rem;">
            🏥 Nueva Admisión de Internación Domiciliaria
          </h2>
          <button class="btn btn-secondary btn-sm" onclick="document.getElementById('modal-nueva-admision').classList.remove('active')">✕</button>
        </div>
        <form id="form-nueva-admision">
          <div class="modal-body" style="display: flex; flex-direction: column; gap: 1.25rem;">
            
            <!-- 1. Datos Personales -->
            <div>
              <h4 style="font-size: 0.9rem; font-weight: 700; color: var(--primary-700); text-transform: uppercase; margin-bottom: 0.75rem; border-bottom: 1px solid var(--border-subtle); padding-bottom: 0.3rem;">
                1. Datos del Paciente y Contacto
              </h4>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Nombre Completo *</label>
                  <input type="text" name="nombreCompleto" class="form-control" required placeholder="Ej: Juan Carlos Pérez">
                </div>
                <div class="form-group">
                  <label class="form-label">DNI *</label>
                  <input type="text" name="dni" class="form-control" required placeholder="Número de documento">
                </div>
                <div class="form-group">
                  <label class="form-label">Fecha de Nacimiento *</label>
                  <input type="date" name="fechaNacimiento" class="form-control" required>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Género</label>
                  <select name="genero" class="form-control">
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">Teléfono de Contacto *</label>
                  <input type="tel" name="telefono" class="form-control" required placeholder="+54 11 ...">
                </div>
                <div class="form-group">
                  <label class="form-label">Nivel de Complejidad *</label>
                  <select name="nivelComplejidad" class="form-control">
                    <option value="Alta Complejidad">Alta Complejidad (Enfermería 12/24hs + Rehab)</option>
                    <option value="Mediana Complejidad" selected>Mediana Complejidad (Kine + Enf. alterna)</option>
                    <option value="Baja Complejidad">Baja Complejidad (Control / Infusiones)</option>
                  </select>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group" style="grid-column: span 2;">
                  <label class="form-label">Dirección Domiciliaria Exacta *</label>
                  <input type="text" name="direccion" class="form-control" required placeholder="Calle, número, piso y depto">
                </div>
                <div class="form-group">
                  <label class="form-label">Localidad / Barrio *</label>
                  <input type="text" name="localidad" class="form-control" required placeholder="Ej: Caballito / Vicente López">
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Indicaciones de Llegada para Móviles Sanitarios</label>
                <input type="text" name="indicacionesLlegada" class="form-control" placeholder="Ej: Entrada por cochera, ascensor amplio apto camilla, timbre 4B">
              </div>
            </div>

            <!-- 2. Cobertura Médica -->
            <div>
              <h4 style="font-size: 0.9rem; font-weight: 700; color: var(--primary-700); text-transform: uppercase; margin-bottom: 0.75rem; border-bottom: 1px solid var(--border-subtle); padding-bottom: 0.3rem;">
                2. Cobertura Médica y Financiador
              </h4>
              <div class="form-row">
                <div class="form-group" style="position: relative;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
                    <label class="form-label" style="margin-bottom: 0;">Obra Social / Financiador *</label>
                    <button type="button" id="btn-nueva-obra-social" class="btn btn-secondary btn-sm" style="padding: 0.15rem 0.5rem; font-size: 0.725rem; line-height: 1.2;" title="Registrar una nueva obra social si falta en la lista">+ Nueva</button>
                  </div>
                  <input type="text" name="obraSocial" id="input-obra-social" list="lista-obras-sociales" class="form-control" autocomplete="off" placeholder="Buscar o escribir (ej: PAMI, OSDE, MEDIFE...)" required>
                  <datalist id="lista-obras-sociales">
                    ${getObrasSocialesList().map(os => `<option value="${os}"></option>`).join('')}
                  </datalist>
                </div>
                <div class="form-group">
                  <label class="form-label">Número de Afiliado</label>
                  <input type="text" name="numeroAfiliado" class="form-control" placeholder="Ej: 150493820100">
                </div>
                <div class="form-group">
                  <label class="form-label">Plan / Estado</label>
                  <input type="text" name="planCobertura" class="form-control" placeholder="Ej: Plan 310 / Autorizado">
                </div>
              </div>
            </div>

            <!-- 3. Cuidador Principal y Responsable Legal -->
            <div>
              <h4 style="font-size: 0.9rem; font-weight: 700; color: var(--primary-700); text-transform: uppercase; margin-bottom: 0.75rem; border-bottom: 1px solid var(--border-subtle); padding-bottom: 0.3rem;">
                3. Cuidador Principal / Contacto de Emergencia
              </h4>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Nombre y Apellido Cuidador</label>
                  <input type="text" name="cuidadorNombre" class="form-control" placeholder="Nombre completo">
                </div>
                <div class="form-group">
                  <label class="form-label">Vínculo con el Paciente</label>
                  <input type="text" name="cuidadorVinculo" class="form-control" placeholder="Ej: Hija / Cónyuge / Cuidador formal">
                </div>
                <div class="form-group">
                  <label class="form-label">Teléfono Cuidador</label>
                  <input type="tel" name="cuidadorTelefono" class="form-control" placeholder="+54 11 ...">
                </div>
              </div>
            </div>

            <!-- 4. Evaluación de Accesibilidad Domiciliaria -->
            <div>
              <h4 style="font-size: 0.9rem; font-weight: 700; color: var(--primary-700); text-transform: uppercase; margin-bottom: 0.75rem; border-bottom: 1px solid var(--border-subtle); padding-bottom: 0.3rem;">
                4. Evaluación de Accesibilidad Edilicia Domiciliaria
              </h4>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.75rem; background: var(--slate-50); padding: 1rem; border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
                <label class="switch-label">
                  <input type="checkbox" name="espacioCamaOrtopedica" class="switch-input" checked>
                  <span>Espacio para Cama Ortopédica</span>
                </label>
                <label class="switch-label">
                  <input type="checkbox" name="electricidadEstable" class="switch-input" checked>
                  <span>Red Eléctrica Estable (Disyuntor)</span>
                </label>
                <label class="switch-label">
                  <input type="checkbox" name="enchufeO2" class="switch-input" checked>
                  <span>Toma 220V Dedicado para O2</span>
                </label>
                <label class="switch-label">
                  <input type="checkbox" name="aguaPotable" class="switch-input" checked>
                  <span>Agua Potable Continua</span>
                </label>
                <label class="switch-label">
                  <input type="checkbox" name="banoAdaptado" class="switch-input">
                  <span>Baño Adaptado / Sin barreras</span>
                </label>
                <label class="switch-label">
                  <input type="checkbox" name="cuidadorResponsableLegal" class="switch-input" checked>
                  <span>Cuidador es Responsable Legal</span>
                </label>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-nueva-admision').classList.remove('active')">Cancelar</button>
            <button type="submit" class="btn btn-primary">Registrar Admisión e Internación</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

async function handleGuardarAdmision(form, onSelectPatient) {
  const formData = new FormData(form);
  const idPaciente = 'PAC-' + Math.floor(100 + Math.random() * 900);

  const nuevoPaciente = {
    id: idPaciente,
    dni: formData.get('dni'),
    nombreCompleto: formData.get('nombreCompleto'),
    fechaNacimiento: formData.get('fechaNacimiento'),
    edad: calcularEdad(formData.get('fechaNacimiento')),
    genero: formData.get('genero'),
    telefono: formData.get('telefono'),
    direccion: formData.get('direccion'),
    localidad: formData.get('localidad'),
    provincia: 'CABA',
    geolocalizacion: '-34.6037, -58.3816',
    indicacionesLlegada: formData.get('indicacionesLlegada'),
    obraSocial: formData.get('obraSocial'),
    numeroAfiliado: formData.get('numeroAfiliado'),
    planCobertura: formData.get('planCobertura'),
    estadoAutorizacion: 'Autorizado',
    nivelComplejidad: formData.get('nivelComplejidad'),
    fechaIngreso: new Date().toISOString().split('T')[0],
    estado: 'Activo',
    diagnosticoPrincipal: { code: 'Z51.5', desc: 'Atención médica domiciliaria programada' },
    diagnosticosSecundarios: [],
    pam: {
      objetivos: 'Ingreso a internación domiciliaria. Estabilización y valoración por equipo interdisciplinario.',
      frecuenciaVisitas: 'Médico (1x/sem), Enfermería (según indicación), Kinesiología (a evaluar)'
    },
    accesibilidad: {
      cuidadorNombre: formData.get('cuidadorNombre'),
      cuidadorVinculo: formData.get('cuidadorVinculo'),
      cuidadorTelefono: formData.get('cuidadorTelefono'),
      cuidadorResponsableLegal: !!formData.get('cuidadorResponsableLegal'),
      tipoVivienda: 'Departamento',
      espacioCamaOrtopedica: !!formData.get('espacioCamaOrtopedica'),
      electricidadEstable: !!formData.get('electricidadEstable'),
      aguaPotable: !!formData.get('aguaPotable'),
      enchufeO2: !!formData.get('enchufeO2'),
      banoAdaptado: !!formData.get('banoAdaptado')
    }
  };

  const osIngresada = (formData.get('obraSocial') || '').trim();
  if (osIngresada) {
    registrarNuevaObraSocial(osIngresada);
  }

  await offlineStore.put('pacientes', nuevoPaciente);
  ModalManager.close('modal-nueva-admision');
  notify.success(`Paciente ${nuevoPaciente.nombreCompleto} admitido con éxito.`);
  onSelectPatient(nuevoPaciente.id);
}

function calcularEdad(fechaNac) {
  if (!fechaNac) return 70;
  const hoy = new Date();
  const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad;
}
